/**
 * React Query hooks for Organization feature
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAllTabs,
  createTab,
  updateTab,
  deleteTab,
  getListsForTab,
  createList,
  updateList,
  deleteList,
  getListAds,
  addAdsToList,
  moveAd,
  reorderAdsInList,
  removeAdFromList,
  searchAds,
  getRulesForList,
  createRule,
  updateRule,
  deleteRule,
  applyRule,
  previewRule,
} from '../api/organization';
import {
  CreateTabRequest,
  UpdateTabRequest,
  CreateListRequest,
  UpdateListRequest,
  AddAdsToListRequest,
  MoveAdRequest,
  ReorderAdsRequest,
  CreateRuleRequest,
  UpdateRuleRequest,
  PreviewRuleRequest,
} from '../types/organization';

// Query keys
export const organizationKeys = {
  all: ['organization'] as const,
  tabs: () => [...organizationKeys.all, 'tabs'] as const,
  tab: (id: string) => [...organizationKeys.tabs(), id] as const,
  lists: (tabId: string) => [...organizationKeys.all, 'lists', tabId] as const,
  listAds: (listId: string) => [...organizationKeys.all, 'ads', listId] as const,
  search: (query: string, tabId: string) => [...organizationKeys.all, 'search', tabId, query] as const,
};

// ==================== TAB HOOKS ====================

export function useAllTabs(enablePolling: boolean = false) {
  return useQuery({
    queryKey: organizationKeys.tabs(),
    queryFn: getAllTabs,
    refetchInterval: enablePolling ? 10000 : false, // Only poll if explicitly enabled
    refetchOnWindowFocus: false,
  });
}

export function useCreateTab() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: CreateTabRequest) => createTab(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.tabs() });
    },
  });
}

export function useUpdateTab() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ tabId, request }: { tabId: string; request: UpdateTabRequest }) => 
      updateTab(tabId, request),
    onSuccess: (_, { tabId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.tab(tabId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.tabs() });
    },
  });
}

export function useDeleteTab() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (tabId: string) => deleteTab(tabId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.tabs() });
    },
  });
}

// ==================== LIST HOOKS ====================

export function useListsForTab(tabId: string | null) {
  return useQuery({
    queryKey: tabId ? organizationKeys.lists(tabId) : ['lists', 'null'],
    queryFn: () => (tabId ? getListsForTab(tabId) : []),
    enabled: !!tabId,
  });
}

export function useCreateList() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ tabId, request }: { tabId: string; request: CreateListRequest }) =>
      createList(tabId, request),
    onSuccess: (_, { tabId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists(tabId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.tabs() });
    },
  });
}

export function useUpdateList() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, tabId, request }: { listId: string; tabId: string; request: UpdateListRequest }) =>
      updateList(listId, request),
    onSuccess: (_, { tabId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists(tabId) });
    },
  });
}

export function useDeleteList() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, tabId }: { listId: string; tabId: string }) => deleteList(listId),
    onSuccess: (_, { tabId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists(tabId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.tabs() });
    },
  });
}

// ==================== AD HOOKS ====================

export function useListAds(listId: string | null) {
  return useQuery({
    queryKey: listId ? organizationKeys.listAds(listId) : ['ads', 'null'],
    queryFn: () => (listId ? getListAds(listId) : []),
    enabled: !!listId,
  });
}

export function useAddAdsToList() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, request }: { listId: string; request: AddAdsToListRequest }) =>
      addAdsToList(listId, request),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.listAds(listId) });
      // Also invalidate all lists to update ad counts
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'lists'] });
    },
  });
}

export function useMoveAd() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ 
      sourceListId, 
      adId, 
      request 
    }: { 
      sourceListId: string; 
      adId: string; 
      request: MoveAdRequest 
    }) => moveAd(sourceListId, adId, request),
    onSuccess: (_, { sourceListId, request }) => {
      // Invalidate both source and target lists
      queryClient.invalidateQueries({ queryKey: organizationKeys.listAds(sourceListId) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.listAds(request.target_list_id) });
      // Invalidate all lists to update ad counts
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'lists'] });
    },
  });
}

export function useReorderAds() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, request }: { listId: string; request: ReorderAdsRequest }) =>
      reorderAdsInList(listId, request),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.listAds(listId) });
    },
  });
}

export function useRemoveAdFromList() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, adId }: { listId: string; adId: string }) =>
      removeAdFromList(listId, adId),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.listAds(listId) });
      // Invalidate all lists to update ad counts
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'lists'] });
    },
  });
}

export function useSearchAds(query: string, tabId: string | null, enabled: boolean = true) {
  return useQuery({
    queryKey: tabId && query ? organizationKeys.search(query, tabId) : ['search', 'null'],
    queryFn: () => (tabId && query ? searchAds(query, tabId) : []),
    enabled: enabled && !!tabId && query.length > 0,
    staleTime: 30000, // Results valid for 30 seconds
  });
}

// ==================== RULE HOOKS ====================

export function useRulesForList(listId: string | null) {
  return useQuery({
    queryKey: listId ? [...organizationKeys.all, 'rules', listId] : ['rules', 'null'],
    queryFn: () => (listId ? getRulesForList(listId) : []),
    enabled: !!listId,
  });
}

export function useCreateRule() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, request }: { listId: string; request: CreateRuleRequest }) =>
      createRule(listId, request),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'rules', listId] });
    },
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, ruleId, request }: { listId: string; ruleId: string; request: UpdateRuleRequest }) =>
      updateRule(listId, ruleId, request),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'rules', listId] });
    },
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, ruleId }: { listId: string; ruleId: string }) =>
      deleteRule(listId, ruleId),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'rules', listId] });
    },
  });
}

export function useApplyRule() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ listId, ruleId }: { listId: string; ruleId: string }) =>
      applyRule(listId, ruleId),
    onSuccess: (_, { listId }) => {
      // Invalidate list ads to show newly added ads
      queryClient.invalidateQueries({ queryKey: organizationKeys.listAds(listId) });
      // Invalidate rules to update last_applied stats
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'rules', listId] });
      // Invalidate lists to update ad counts
      queryClient.invalidateQueries({ queryKey: [...organizationKeys.all, 'lists'] });
    },
  });
}

export function usePreviewRule() {
  return useMutation({
    mutationFn: (request: PreviewRuleRequest) => previewRule(request),
  });
}

